import { useState, type ReactElement } from "react";
import {
  Checkbox,
  FormField,
  Input,
  NumberInput,
  Select,
} from "@playbookai/ui";
import type { ConnectorManifest } from "@playbookai/api-clients";
import {
  settingsFields,
  settingsFieldError,
  type SettingsFieldModel,
} from "./manifest-render.js";

interface ManifestSettingsFormProps {
  readonly manifest: ConnectorManifest;
  readonly values: Readonly<Record<string, unknown>>;
  readonly onChange: (key: string, value: unknown) => void;
  /**
   * Which of the manifest's declared settings to render. Defaults to `all`.
   *
   * `required` exists for the add flow, which asks for the minimum that lets an
   * integration be created and leaves everything else to the Overview tab that
   * already owns it. The subset is the MANIFEST's own `required` list, so this
   * stays a projection rather than a judgement: no field is hidden because the
   * shell decided it was unimportant.
   */
  readonly fields?: "all" | "required";
}

/**
 * The settings form, generated from the manifest's `settingsSchema`.
 *
 * Every label, every control type and every option list comes from the
 * manifest. There is no per-provider form component and there is no map from a
 * provider key to a field list — a connector that ships next quarter renders
 * here on the first request.
 *
 * A `secret` field renders NOTHING. PB-4734 made the OAuth application the
 * platform's, so a tenant administrator never enters, views or updates a client
 * secret or a reference to one — there is no route that would accept it. A
 * manifest may still declare `format: "secret"`, and the honest response is to
 * skip the field rather than offer a box whose submission the server rejects.
 */
/**
 * Exactly the fields this form will render, for a caller that has to agree with
 * it — the add flow gates its Create button on them being answered.
 *
 * Exported so that agreement is by CONSTRUCTION rather than by two copies of
 * the same two filters. A caller reproducing "required, and not a secret" would
 * drift the first time either rule moved, and the symptom would be a submit
 * button that is disabled with every visible field filled in.
 */
export function renderableSettingsFields(
  manifest: ConnectorManifest,
  scope: "all" | "required" = "all",
): SettingsFieldModel[] {
  return settingsFields(manifest).filter(
    (field) =>
      field.control !== "secret" && (scope === "all" || field.required),
  );
}

export function ManifestSettingsForm({
  manifest,
  values,
  onChange,
  fields: scope = "all",
}: ManifestSettingsFormProps): ReactElement {
  return (
    <div className="space-y-4">
      {renderableSettingsFields(manifest, scope).map((field) => (
        <SettingField
          key={field.key}
          field={field}
          value={values[field.key]}
          onChange={(value) => onChange(field.key, value)}
        />
      ))}
    </div>
  );
}

function SettingField({
  field,
  value,
  onChange,
}: {
  field: SettingsFieldModel;
  value: unknown;
  onChange: (value: unknown) => void;
}): ReactElement {
  const id = `setting-${field.key}`;

  /* Shown once the field has been LEFT, not while it is being typed: an
     administrator three characters into a URL is not making a mistake yet, and
     a message that appears on keystroke one reads as the form arguing with
     them. After a blur it is a genuine correction, and it clears as soon as the
     value is right. Local to the field — nothing above needs to know.

     Wired to every editable control, not just the text one. It started on
     `Input` alone, which quietly meant a required choice or number field could
     be invalid and say nothing. */
  const [touched, setTouched] = useState(false);
  const error = settingsFieldError(field, value);
  const shownError = touched ? error : undefined;

  if (field.control === "choice") {
    return (
      <div onBlurCapture={() => setTouched(true)}>
        <FormField
          label={field.label}
          htmlFor={id}
          required={field.required}
          hint={field.description}
          error={shownError}
        >
          <Select
            id={id}
            value={typeof value === "string" ? value : ""}
            onChange={(next) => onChange(next)}
            options={[
              { value: "", label: "—" },
              ...(field.options ?? []).map((option) => ({
                value: option,
                label: option,
              })),
            ]}
          />
        </FormField>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" onBlurCapture={() => setTouched(true)}>
      <label htmlFor={id} className="block text-sm font-medium">
        {field.label}
        {field.required ? (
          <span className="text-pb-red-text ml-0.5" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {/* The description is passed to each control as `hint` below rather than
          rendered here, so the primitive wires it with `aria-describedby` and a
          screen reader actually announces it — the same reason the create form
          switched. `Checkbox` has no `hint` prop, so that one branch keeps the
          paragraph and is the only field where the text stays decorative. */}
      {field.description && field.control === "boolean" ? (
        <p className="text-xs text-pb-text-muted">{field.description}</p>
      ) : null}

      {field.control === "boolean" ? (
        /* No `label` of its own: the shared label above already renders
           `field.label`, and passing it here printed the field name twice. */
        <Checkbox
          id={id}
          checked={value === true}
          onChange={(checked) => onChange(checked)}
        />
      ) : field.control === "number" ? (
        <NumberInput
          id={id}
          value={typeof value === "number" ? value : ""}
          onChange={(next) => onChange(next === "" ? undefined : next)}
          onBlur={() => setTouched(true)}
          {...(field.description === undefined
            ? {}
            : { hint: field.description })}
          {...(shownError === undefined ? {} : { error: shownError })}
        />
      ) : (
        <Input
          id={id}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => setTouched(true)}
          {...(field.description === undefined
            ? {}
            : { hint: field.description })}
          {...(shownError === undefined ? {} : { error: shownError })}
        />
      )}
    </div>
  );
}
